import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Eye, EyeOff, Mail, Lock, HelpCircle, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import Logo from './Logo';
import LanguageSelector from './LanguageSelector';
import './Login.css';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const { login, resetPassword, signInWithGoogle } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }

    try {
      setLoading(true);
      await login(email, password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error en login:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    try {
      setLoading(true);
      await signInWithGoogle();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error en Google Sign-In:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    
    if (!resetEmail) {
      setResetMessage({ type: 'error', text: t('login.resetEmailRequired') });
      return;
    }

    try {
      setResetLoading(true);
      setResetMessage('');
      await resetPassword(resetEmail);
      setResetMessage({ 
        type: 'success', 
        text: t('login.resetPasswordSuccess')
      });
    } catch (error) {
      console.error('Error al enviar correo de recuperación:', error);
      setResetMessage({ 
        type: 'error', 
        text: t('login.resetPasswordError')
      });
    } finally {
      setResetLoading(false);
    }
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        {!showForgotPassword ? (
          <>
            <div className="auth-header">
              <LanguageSelector />
              <Logo size="auth-logo" className="auth-logo" />
              <h2>{t('login.title')}</h2>
              <p>{t('login.subtitle')}</p>
              <button 
                type="button" 
                className="back-to-landing-link"
                onClick={() => (window.location.href = 'https://rtb-collect.com')}
              >
                <ArrowLeft size={16} /> {t('login.backToLanding')}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {/* Google Auth como primera opción */}
              <button
                type="button"
                className="google-signin-button"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loading ? t('login.signingIn') : t('login.continueWithGoogle')}
              </button>

              <div className="auth-divider">
                <span>o</span>
              </div>

              {/* Formulario de email y contraseña */}
              <div className="form-group">
                <label htmlFor="email">{t('common.email')}</label>
                <div className="input-wrapper">
                  <Mail size={20} className="input-icon" />
                  <input
                    id="email"
                    type="email"
                    className="input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.emailPlaceholder')}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">{t('common.password')}</label>
                <div className="input-wrapper">
                  <Lock size={20} className="input-icon" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.passwordPlaceholder')}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button 
                type="button"
                className="forgot-password-link"
                onClick={() => setShowForgotPassword(true)}
              >
                {t('login.forgotPassword')}
              </button>

              <button
                type="submit"
                className="btn btn-primary auth-button"
                disabled={loading}
              >
                {loading ? t('login.signingIn') : t('login.signIn')}
              </button>
            </form>

            <div className="auth-footer">
              <p>
                {t('login.noAccount')}{' '}
                <Link to="/register" className="auth-link">
                  {t('login.registerHere')}
                </Link>
              </p>
              <button 
                className="how-it-works-button"
                onClick={() => window.open('/how-it-works', '_blank')}
              >
                <HelpCircle size={16} />
                {t('login.howItWorks')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="auth-header">
              <LanguageSelector />
              <Logo size="auth-logo" className="auth-logo" />
              <h2>{t('login.resetPasswordTitle')}</h2>
              <p>{t('login.resetPasswordSubtitle')}</p>
            </div>

            <form onSubmit={handleResetPassword} className="auth-form">
              <div className="form-group">
                <label htmlFor="resetEmail">{t('common.email')}</label>
                <div className="input-wrapper">
                  <Mail size={20} className="input-icon" />
                  <input
                    id="resetEmail"
                    type="email"
                    className="input"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder={t('login.resetEmailPlaceholder')}
                    required
                  />
                </div>
              </div>

              {resetMessage && (
                <div className={`reset-message ${resetMessage.type}`}>
                  {resetMessage.type === 'success' ? (
                    <CheckCircle size={20} />
                  ) : (
                    <AlertCircle size={20} />
                  )}
                  <span>{resetMessage.text}</span>
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary auth-button"
                disabled={resetLoading}
              >
                {resetLoading ? t('login.sending') : t('login.sendResetLink')}
              </button>
            </form>

            <div className="auth-footer">
              <button 
                className="back-to-login-link"
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetMessage('');
                  setResetEmail('');
                }}
              >
                <ArrowLeft size={16} />
                {t('login.backToLogin')}
              </button>
              <button 
                className="how-it-works-button"
                onClick={() => window.open('/how-it-works', '_blank')}
              >
                <HelpCircle size={16} />
                {t('login.howItWorks')}
              </button>
            </div>
          </>
        )}
      </div>
      
      {/* Panel de imágenes lateral */}
      <div className="auth-image-panel">
        <div className="auth-image-background" style={{ backgroundImage: `url('/monterrey-bbva.jpeg')` }}>
          <div className="dark-gradient"></div>
          <img src="/rtb-monterrey.jpg" alt="RTB Monterrey" className="auth-overlay-image" />
        </div>
      </div>
    </div>
  );
}
